import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { fromJS, List, Map } from 'immutable';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { v4 } from 'uuid';
import { ancestorHasClass } from '../../../../utils/helpers/dom.helper';
import { compactSpaces } from '../../../../utils/helpers/string.helper';
import { Client } from '../../../client-data/client.model';
import {
  DIALOG_CLASS,
  DialogActionsEnum,
  IDialogTrigger,
} from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { DocumentReferenceTypesEnum } from '../../../document-data/document-data.data';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import { COMMON_FIELDS_KEYS } from '../../../entity-data/entity.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { AccountQuotas } from '../../../features-data/sub-models/account-quotas.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { DIALOG_TYPE as growthDialog } from '../../../features/components/growth-dialog/growth-dialog.constant';
import { LocationInfo } from '../../../geolocation-data/models/location-info.model';
import { AddressData } from '../../../geolocation-data/models/address-data.model';
import { Settings } from '../../../settings-data/settings.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { AUTOCOMPLETE_CLASS } from '../../../shared/directives/auto-complete/auto-complete.directive';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
} from '../../../shared/directives/auto-complete/auto-complete.interface';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DIALOG_TYPE as shippingInfoDialog } from '../shipping-info-dialog/shipping-info-dialog.component';
import {
  AddClientBuilder,
  IClientAutoCompleteOptions,
} from './add-client.builder';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { GlobalBannerTypesEnum } from '../../../shared/components/global-banner/global-banner.enum';
import { EMAIL_MAX_LENGTH_MULTIPLE } from '../../../shared/services/email/email.service';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { TranslateService } from '@ngx-translate/core';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import { AchCreditBankDetails } from '../../../document-data/sub-models/ach-credit-bank-details.model';

export enum AddClientActionsEnum {
  close_form = 'close_form',
  show_form = 'show_form',
}

export interface IAddClientActionTrigger {
  action: AddClientActionsEnum;
}

export interface IAddClientData {
  billingInfo: Map<string, any>;
  shippingInfo: Map<string, any>;
  terms: number;
  reference: Reference;
  hasEmailChanges: boolean;
  hasMobileChanges: boolean;
  achCreditBankDetails: AchCreditBankDetails;
}

@Component({
  selector: 'app-add-client',
  templateUrl: 'add-client.component.html',
  styleUrls: ['add-client.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AddClientBuilder],
})
export class AddClientComponent implements OnInit, OnChanges, OnDestroy {
  @Input() actionTriggers$: Observable<IAddClientActionTrigger>;
  @Input() billing: Billing;
  @Input() shipping: Shipping;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() accountSettings: Settings;
  @Input() enableShipping: boolean;
  @Input() condensed: boolean;
  @Input() secondaryAction: string;
  @Input() enableHandleClickOutside: boolean;
  @Input() preventEditExistingClient: boolean;
  @Input() preventDeleteClient: boolean;
  @Input() enableMultipleEmails: boolean;
  @Input() featureToggles: Toggles;
  @Input() locationInfo: LocationInfo;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() secondaryTrackingAction: ITrackingAction;

  @Output() onEditMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  onUpdateClient: EventEmitter<IAddClientData> = new EventEmitter<IAddClientData>();
  @Output() onNewClient: EventEmitter<Client> = new EventEmitter<Client>();
  @Output() onSecondaryAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDeleteClient: EventEmitter<void> = new EventEmitter<void>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();

  private _el: any;
  private _emptyForm: any;
  private _emptyAutoCompleteOptions: IClientAutoCompleteOptions;
  private _clientAutoCompleteOptions: IClientAutoCompleteOptions;
  private _originalFormValue: any;
  private _nameFocusableTrigger: Subject<void>;
  private _destroying: boolean;
  private _settingClient: boolean;
  private _isUniqueClient: boolean;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _billingAddressData: AddressData = null;
  private _shippingAddressData: AddressData = null;
  private _subs: Subscription[] = [];
  private _emptyShipping: Shipping;
  private _emailMaxLengthBannerId: string = v4();
  private _achCreditBankDetails: AchCreditBankDetails;
  private _handleClickOutsideCallback = this._handleClickOutside.bind(this);

  editMode: boolean;
  formKeys: any;
  form: FormGroup;
  reference: Reference;
  autoCompleteOptions: IClientAutoCompleteOptions;
  addClientIcon: IIcon;
  addEmailIcon: IIcon;
  deleteEmailIcon: IIcon;
  loadingSpinnerIcon: IIcon;
  nameFocusableTrigger$: Observable<void>;
  fetchingResults$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  addClientTapped: ITrackingAction;
  editClientTapped: ITrackingAction;
  addShippingTapped: ITrackingAction;
  deleteClientTapped: ITrackingAction;
  saveClientTapped: ITrackingAction;
  billingNameTapped: ITrackingAction;
  emailTapped: ITrackingAction;
  mobileTapped: ITrackingAction;
  additionalEmailTapped: ITrackingAction;
  addAdditionalEmailTapped: ITrackingAction;
  removeAdditionalEmailTapped: ITrackingAction;
  billingAddressTapped: ITrackingAction;
  shippingAddressTapped: ITrackingAction;

  constructor(
    private _dialogService: DialogService,
    private _addClientBuilder: AddClientBuilder,
    private _loggerService: LoggerService,
    private _globalBannerService: GlobalBannerService,
    private _translateService: TranslateService,
    private _elementRef: ElementRef,
    private _featuresDataStore: FeaturesDataStore,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.billing) {
      this.createReference(
        this.billing &&
          this.billing.reference &&
          this.billing.reference.hasValue()
          ? this.billing.reference
          : new Reference({
              [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
              [Reference.KEYS.REFERENCE_LOCAL_ID]: v4(),
            })
      );
    }
  }

  ngOnInit() {
    this._el = this._elementRef.nativeElement;
    this._nameFocusableTrigger = new Subject<void>();

    this.formKeys = this._addClientBuilder.FORM_KEYS;
    this.addClientIcon = {
      name: 'add-client',
      size: IconSizesEnum.medium,
    };
    this.addEmailIcon = {
      name: 'add-items',
      size: IconSizesEnum.medium,
    };
    this.deleteEmailIcon = {
      name: 'close',
      size: IconSizesEnum.small,
    };
    this.loadingSpinnerIcon = {
      name: 'loading-spinner',
      size: IconSizesEnum.medium,
    };
    this.addClientTapped = this._trackingActionBuilder.buildButtonTapped(
      'add_client',
      this.trackingScreenName
    );
    this.addShippingTapped = this._trackingActionBuilder.buildButtonTapped(
      'shipping_info',
      this.trackingScreenName
    );
    this.deleteClientTapped = this._trackingActionBuilder.buildButtonTapped(
      'delete_client',
      this.trackingScreenName
    );
    this.saveClientTapped = this._trackingActionBuilder.buildButtonTapped(
      'save_client',
      this.trackingScreenName
    );
    this.billingNameTapped = this._trackingActionBuilder.buildButtonTapped(
      'billing_name_field',
      this.trackingScreenName
    );
    this.emailTapped = this._trackingActionBuilder.buildButtonTapped(
      'email_field',
      this.trackingScreenName
    );
    this.additionalEmailTapped = this._trackingActionBuilder.buildButtonTapped(
      'additional_email_field',
      this.trackingScreenName
    );
    this.addAdditionalEmailTapped = this._trackingActionBuilder.buildButtonTapped(
      'add_additional_email',
      this.trackingScreenName
    );
    this.removeAdditionalEmailTapped = this._trackingActionBuilder.buildButtonTapped(
      'delete_email',
      this.trackingScreenName
    );
    this.billingAddressTapped = this._trackingActionBuilder.buildButtonTapped(
      'billing_address_field',
      this.trackingScreenName
    );
    this.shippingAddressTapped = this._trackingActionBuilder.buildButtonTapped(
      'shipping_address_field',
      this.trackingScreenName
    );
    this.mobileTapped = this._trackingActionBuilder.buildButtonTapped(
      'mobile_phone_field',
      this.trackingScreenName
    );
    this.nameFocusableTrigger$ = this._nameFocusableTrigger.asObservable();

    this.actionTriggers$
      .pipe(
        takeUntil(this._destroyed),
        filter((actionTrigger: IAddClientActionTrigger) => {
          return (
            !this.editMode &&
            actionTrigger &&
            actionTrigger.action === AddClientActionsEnum.show_form
          );
        })
      )
      .subscribe(() => this.toggleForm(true));

    this.actionTriggers$
      .pipe(
        takeUntil(this._destroyed),
        filter((actionTrigger: IAddClientActionTrigger) => {
          return (
            this.editMode &&
            this.form &&
            actionTrigger &&
            actionTrigger.action === AddClientActionsEnum.close_form
          );
        })
      )
      .subscribe(() => {
        const clientSaved = this.saveDocumentClient();

        if (!clientSaved) {
          this.editMode = true;
          this.onEditMode.emit(this.editMode);
        }
      });
  }

  buildForm() {
    if (!this.form) {
      this._emptyAutoCompleteOptions = this._addClientBuilder.buildEmptyAutoCompleteOptions();
      this._clientAutoCompleteOptions = this._addClientBuilder.buildAutoCompleteOptions();
      this._emptyForm = this._addClientBuilder.buildForm(
        new Billing({}),
        null,
        this.documentSettings,
        this.enableMultipleEmails
      ).value;
    }

    this.form = this._addClientBuilder.buildForm(
      this.billing,
      this.shipping,
      this.documentSettings,
      this.enableMultipleEmails
    );
    this._emptyShipping = new Shipping({});
    this._originalFormValue = this.form.value;
    this._billingAddressData = this.billing ? this.billing.addressData : null;
    this._shippingAddressData = this.shipping
      ? this.shipping.addressData
      : null;

    const formKeys = this.formKeys;
    const nameField = this.form.get(formKeys.NAME);
    const emailArrayField = this.form.get(formKeys.EMAIL) as FormArray;
    const billingAddressField = this.form.get(formKeys.BILLING_ADDRESS);
    const shippingAddressField = this.form.get(formKeys.SHIPPING_ADDRESS);
    const useSameBillingShippingControl = this.form.get(
      formKeys.USE_SAME_BILLING_SHIPPING
    );

    if (
      !nameField.value &&
      !this._addClientBuilder.buildEmailValue(emailArrayField.value)
    ) {
      this.autoCompleteOptions = this._clientAutoCompleteOptions;
    } else {
      this.autoCompleteOptions = this._emptyAutoCompleteOptions;
    }

    this._subs.forEach((sub) => sub.unsubscribe());

    this._subs = [];

    this._subs.push(
      useSameBillingShippingControl.valueChanges
        .pipe(
          startWith(useSameBillingShippingControl.value),
          takeUntil(this._destroyed)
        )
        .subscribe(() => this.checkShippingToggle()),
      combineLatest([
        nameField.valueChanges.pipe(startWith(nameField.value)),
        emailArrayField.valueChanges.pipe(
          startWith(emailArrayField.value),
          map((emails: string[]) =>
            this._addClientBuilder.buildEmailValue(emails)
          )
        ),
      ])
        .pipe(
          takeUntil(this._destroyed),
          skip(1),
          filter(([name, email]: any[]) => {
            return (
              !name &&
              !email &&
              !this.autoCompleteOptions[formKeys.NAME] &&
              !this.autoCompleteOptions[formKeys.EMAIL] &&
              !this.autoCompleteOptions[formKeys.MOBILE]
            );
          })
        )
        .subscribe(() => {
          this.autoCompleteOptions = this._clientAutoCompleteOptions;

          this._changeDetectorRef.markForCheck();
        }),
      billingAddressField.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => (this._billingAddressData = null)),
      shippingAddressField.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => (this._shippingAddressData = null)),
      emailArrayField.statusChanges
        .pipe(distinctUntilChanged(), takeUntil(this._destroyed))
        .subscribe(() => {
          const errors = emailArrayField.errors || {};

          if (errors.maxlength) {
            this._globalBannerService.showBanner({
              type: GlobalBannerTypesEnum.error,
              id: this._emailMaxLengthBannerId,
              message: this._translateService.instant(
                `ERROR.FIELD_EMAIL_MAX_LENGTH`,
                {
                  max: EMAIL_MAX_LENGTH_MULTIPLE,
                }
              ),
              dismissible: true,
            });
          } else {
            this._globalBannerService.hideBanner(this._emailMaxLengthBannerId);
          }
        })
    );
  }

  get emailFormArray(): FormArray {
    return this.form.get(this.formKeys.EMAIL) as FormArray;
  }

  addEmailField(evt: Event) {
    evt.stopPropagation();

    this.emailFormArray.push(this._addClientBuilder.buildEmailFormControl(''));
  }

  deleteEmail(evt: Event, position: number) {
    evt.stopPropagation();

    this.emailFormArray.removeAt(position);
  }

  clearAutoCompleteOptions() {
    const nameControl = this.form.get(this.formKeys.NAME);
    const emailControl = this.form.get(this.formKeys.EMAIL);

    if (
      (nameControl.value ||
        this._addClientBuilder.buildEmailValue(emailControl.value)) &&
      !isEqual(this.autoCompleteOptions, this._emptyAutoCompleteOptions)
    ) {
      this.autoCompleteOptions = this._emptyAutoCompleteOptions;

      this._changeDetectorRef.markForCheck();
    } else if (
      isEqual(this.autoCompleteOptions, this._emptyAutoCompleteOptions)
    ) {
      this.autoCompleteOptions = this._clientAutoCompleteOptions;

      this._changeDetectorRef.markForCheck();
    }
  }

  onAutoCompleteSet({ value }: IAutoCompleteOnSet) {
    this._loggerService.log('autocomplete add client select:', value.toJS());

    const client: Client = value;
    const form = this.form;
    const formKeys = this.formKeys;
    const onlySelf = { onlySelf: true };
    const nameField = form.get(formKeys.NAME);
    const emailField = form.get(formKeys.EMAIL) as FormArray;
    const mobileField = form.get(formKeys.MOBILE);

    const termsField = form.get(formKeys.TERMS);
    const shippingAddressField = form.get(formKeys.SHIPPING_ADDRESS);
    const billingAddressField = form.get(formKeys.BILLING_ADDRESS);
    const useSameBillingShippingField = form.get(
      formKeys.USE_SAME_BILLING_SHIPPING
    );
    const emails = this.enableMultipleEmails
      ? this._addClientBuilder.buildEmailFormValue(client.email)
      : [client.email || ''];

    this.createReference(this._addClientBuilder.buildReference(client));

    mobileField.setValue(client.mobile, onlySelf);
    nameField.setValue(client.billingName, onlySelf);
    termsField.setValue(client.terms, onlySelf);
    billingAddressField.setValue(client.billingAddress, onlySelf);

    emailField.controls = [];
    emails.forEach((email: string) =>
      emailField.push(this._addClientBuilder.buildEmailFormControl(email))
    );

    if (shippingAddressField) {
      shippingAddressField.setValue(client.shippingAddress, onlySelf);

      if (
        compactSpaces(shippingAddressField.value) ===
        compactSpaces(billingAddressField.value)
      ) {
        useSameBillingShippingField.setValue(true, onlySelf);
      }
    }

    form.get(formKeys.PHONE).setValue(client.phone);
    form.get(formKeys.MOBILE).setValue(client.mobile);

    form.markAsDirty();
    form.updateValueAndValidity();

    this._loggerService.log('selecting client:', form.value);

    this._originalFormValue = form.value;
    this._billingAddressData = client.billingAddressData;
    this._shippingAddressData = client.shippingAddressData;
    this._achCreditBankDetails = client.achCreditBankDetails;
    this._settingClient = true;

    if (
      this.preventEditExistingClient &&
      this.reference &&
      this.reference.hasValue()
    ) {
      form.disable({ emitEvent: false });
    }

    this.clearSearchLoadingSpinner();

    this._changeDetectorRef.markForCheck();
  }

  onAutoCompleteFetchStart(formKey: string) {
    this.fetchingResults$.next(formKey);
  }

  onAutoCompleteFetchEnd(formKey: string, { list }: IAutoCompleteOnFetchEnd) {
    this.clearSearchLoadingSpinner();

    if (list) {
      const formKeys = this.formKeys;
      const value: string = this.form.get(formKey).value || '';
      const filteredResults: List<Client> = list.filter((client: Client) => {
        const clientValue =
          (formKey === formKeys.NAME ? client.billingName : client.email) || '';

        return value !== clientValue;
      }) as List<Client>;

      this._isUniqueClient = filteredResults.isEmpty();
    }
  }

  clearSearchLoadingSpinner() {
    // set timeout so that we can trigger another change detection cycle
    setTimeout(() => this.fetchingResults$.next(null));
  }

  clearForm(forceClear: boolean = false) {
    this._loggerService.log('clearing the add client form');

    const form = this.form;

    if (form) {
      if (form.dirty || forceClear) {
        const emailArrayField = form.get(this.formKeys.EMAIL) as FormArray;

        emailArrayField.controls = [];
        emailArrayField.push(this._addClientBuilder.buildEmailFormControl(''));

        form.reset(this._emptyForm, { emitEvent: false });
        form.markAsPristine();
      }

      form.enable({ emitEvent: false });
    }
  }

  saveDocumentClient() {
    if (this.form && !this.fetchingResults$.value) {
      if (this.form.disabled) {
        this.form.enable({ emitEvent: false });
      }

      if (this.form.dirty) {
        if (
          isEqual(this.form.value, this._emptyForm) &&
          this._emptyShipping &&
          (!this.shipping || this._emptyShipping.equals(this.shipping))
        ) {
          this.toggleForm(false);
          this.deleteClient();

          return true;
        } else if (this.form.valid) {
          this._featuresDataStore
            .canCreate(AccountQuotas.KEYS.CLIENTS)
            .pipe(takeUntil(this._destroyed))
            .subscribe((withinQuotaLimits: boolean) => {
              if (
                !this.reference ||
                (this.reference && !this.reference.hasValue())
              ) {
                this.createReference(
                  new Reference({
                    [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
                    [Reference.KEYS.REFERENCE_LOCAL_ID]: v4(),
                  })
                );
              }

              const contents: Map<string, any> = fromJS(this.form.value);
              const billingInfo: Map<
                string,
                any
              > = this._addClientBuilder.buildBillingProps(
                contents,
                this._billingAddressData,
                this.reference
              );
              const shippingInfo: Map<
                string,
                any
              > = this._addClientBuilder.buildShippingProps(
                this.documentSettings.showShipping ? contents : null,
                this._shippingAddressData
              );
              const referenceId = this.reference.id;

              // if billing already references existing client or quota allows for client creation
              if (referenceId || withinQuotaLimits) {
                this._loggerService.log('saving document client');

                const formKeys = this.formKeys;
                const client: Client = this._addClientBuilder.buildClient(
                  billingInfo,
                  shippingInfo,
                  this.accountSettings
                );
                const emailValue = contents
                  .get(formKeys.EMAIL)
                  .filter((x) => !!x);
                const originalEmailValue = fromJS(this._originalFormValue)
                  .get(formKeys.EMAIL)
                  .filter((x) => !!x);
                const hasEmailChanges =
                  referenceId && !emailValue.equals(originalEmailValue);

                const mobileValue = contents.get(formKeys.MOBILE);
                const originalMobileValue = fromJS(this._originalFormValue).get(
                  formKeys.MOBILE
                );
                const hasMobileChanges =
                  referenceId && mobileValue !== originalMobileValue;

                this.onUpdateClient.emit({
                  billingInfo,
                  shippingInfo,
                  hasEmailChanges,
                  hasMobileChanges,
                  reference: client ? this.reference : null,
                  terms: contents.get(formKeys.TERMS),
                  achCreditBankDetails: this._achCreditBankDetails,
                });

                this._emptyShipping = this.shipping;

                if (!referenceId && client) {
                  this.onNewClient.emit(
                    client.set(
                      COMMON_FIELDS_KEYS.LOCAL_ID,
                      this.reference.referenceLocalId
                    ) as Client
                  );
                }

                this.toggleForm(false);
              } else if (
                !withinQuotaLimits &&
                (this._isUniqueClient || !referenceId) &&
                !this._destroying
              ) {
                this._document.removeEventListener(
                  'click',
                  this._handleClickOutsideCallback
                );

                this._dialogService.openGrowthDialog(
                  BannerCanvasTypesEnum.quota_reached,
                  AccountQuotas.KEYS.CLIENTS,
                  () => {
                    this.clearForm();

                    this.onGrowthCta.emit.bind(this.onGrowthCta);
                  }
                );

                this._dialogService.dialogTriggers$
                  .pipe(
                    filter((dialogTrigger: IDialogTrigger) => {
                      const dialogTriggerData = dialogTrigger.data;

                      return (
                        dialogTrigger.action === DialogActionsEnum.close &&
                        dialogTrigger.type === growthDialog &&
                        dialogTriggerData &&
                        dialogTrigger.data.result !== true
                      );
                    }),
                    takeUntil(this._destroyed),
                    take(1)
                  )
                  .subscribe(() => {
                    this.clearForm();

                    setTimeout(() =>
                      this._document.addEventListener(
                        'click',
                        this._handleClickOutsideCallback
                      )
                    );
                  });
              }
            });

          return true;
        }
      }

      this.toggleForm(false);

      return true;
    }

    return false;
  }

  toggleForm(flag: boolean) {
    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );

    this.editMode = flag;
    this.onEditMode.emit(this.editMode);

    if (flag) {
      this.buildForm();

      if (this.preventEditExistingClient) {
        this.clearForm(true);
        this.createReference(null);
        this.clearAutoCompleteOptions();
      }

      setTimeout(() =>
        this._document.addEventListener(
          'click',
          this._handleClickOutsideCallback
        )
      );

      setTimeout(() => this._nameFocusableTrigger.next(), 50);
    } else if (this.form) {
      this.form.markAsPristine();
    }

    this._changeDetectorRef.markForCheck();
  }

  checkShippingToggle() {
    if (this.form.get(this.formKeys.USE_SAME_BILLING_SHIPPING).value) {
      const shippingAddressControl = this.form.get(
        this.formKeys.SHIPPING_ADDRESS
      );

      shippingAddressControl.markAsDirty();
      shippingAddressControl.setValue(
        this.form.get(this.formKeys.BILLING_ADDRESS).value,
        {
          onlySelf: true,
        }
      );
    }
  }

  deleteClient(evt?: Event) {
    if (evt) {
      evt.stopPropagation();
    }

    this.clearForm();

    this.onDeleteClient.emit();
  }

  onSecondaryActionClick() {
    this.clearForm();

    this.onSecondaryAction.emit();
  }

  onAddShippingInfo() {
    this._dialogService.openDialog({ type: shippingInfoDialog });
  }

  createReference(reference: Reference) {
    if (reference) {
      this.reference = reference;
      this.editClientTapped = this._trackingActionBuilder.buildInputTapped(
        reference.id,
        reference.referenceLocalId,
        TrackingInputTypeValuesEnum.client,
        this.trackingScreenName
      );
    } else {
      this.reference = null;
      this.editClientTapped = null;
    }

    this._changeDetectorRef.markForCheck();
  }

  clearExistingClient(evt: Event) {
    if (
      this.preventEditExistingClient &&
      this.reference &&
      !this._settingClient
    ) {
      evt.stopPropagation();

      this.clearForm();
      this.createReference(null);
      this.clearAutoCompleteOptions();

      setTimeout(() => this._nameFocusableTrigger.next(), 50);
    }

    if (this._settingClient) {
      this._settingClient = false;
    }
  }

  ngOnDestroy() {
    this._destroying = true;

    this._dialogService.closeDialog({ type: shippingInfoDialog });

    if (this.preventEditExistingClient) {
      this.clearForm();
    } else {
      this.saveDocumentClient();
    }

    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _handleClickOutside(evt: Event) {
    if (this.enableHandleClickOutside) {
      const evtTarget = evt.target;
      const dialogOverlay = this._document.querySelector(DIALOG_CLASS);

      if (!evtTarget) {
        return;
      }

      if (
        !(dialogOverlay && dialogOverlay.contains(evtTarget as any)) &&
        !this._el.contains(evtTarget) &&
        !ancestorHasClass(evtTarget, AUTOCOMPLETE_CLASS)
      ) {
        this.saveDocumentClient();
      }
    }
  }
}
